import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Download, Star, Play, Pickaxe, Target, Radio, FileText, Sparkles, Compass, Network, BarChart3, Search, Layers, Clock, Activity as ActivityIcon } from "lucide-react";
import { usePdfStore } from "@/stores/pdf-store";
import { useTrawlStore } from "@/stores/trawl-store";
import { useScoringStore } from "@/stores/scoring-store";
import { useCancelScoringBatch, useScoringBatchStatus } from "@/hooks/useScoringBatchStatus";
import { useSnowballStore } from "@/stores/snowball-store";
import { useClusteringStore } from "@/stores/clustering-store";
import { useMonitorStore } from "@/stores/monitor-store";
import { useBibtexImportStore } from "@/stores/bibtex-import-store";
import { useSmartTrawlStore } from "@/stores/smart-trawl-store";
import { useSeedDiscoveryStore } from "@/stores/seed-discovery-store";
import { useFieldDebateGenerationStore } from "@/stores/field-debate-generation-store";
import { useGapAnalysisGenerationStore } from "@/stores/gap-analysis-generation-store";
import { useGapAnalysisStore } from "@/stores/gap-analysis-store";
import { useGapFillStore } from "@/stores/gap-fill-store";
import { useZoteroBulkImportStore } from "@/stores/zotero-bulk-import-store";
import { cancelBulkImport } from "@/hooks/useZoteroBulkImport";
import { useQueryClient } from "@tanstack/react-query";
import { useConcept } from "@/hooks/useConcepts";
import { useRetryAngleRescore } from "@/hooks/useFacets";
import { useRunEstimate } from "@/hooks/useRunEstimate";
import type { PaperListResponse } from "@/types/paper";
import { formatUsual, isCancelMessage } from "@/lib/opTiming";
import apiClient from "@/lib/api-client";
import { useActivity, type JobRun } from "@/hooks/useActivity";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { CANCELLABLE, RUN_AGAIN_PATH, STORE_OP, invalidateAfter, kindLabel } from "@/lib/jobRuns";
import { toast } from "@/lib/toast";
import { announceQueued, isQueued } from "@/lib/queuedStart";
import { unreadCount } from "@/lib/activityRows";
import { toastJobFinished } from "@/lib/runTiming";
import { useActivityStore } from "@/stores/activity-store";
import { OperationPill, type OperationStatus } from "./OperationPill";
import { ActivityPanel, type LiveOp } from "./ActivityPanel";

/** Ops whose runs the server records (GET /activity lists them), so the
 * session list skips them to avoid showing each run twice. */
const SERVER_RECORDED = new Set<string>(["background-batch", "angle-rescore", "monitor", "field-debate"]);
const NO_RUNS: JobRun[] = [];
/** The FD endpoints map at most ~400 papers (scoped set). */
const FD_SCOPE_CAP = 400;
/** Batch builds can take hours; stop waiting for a landed row after this. */
const FD_LANDED_GIVE_UP_MS = 6 * 3600_000;

/** Server-backed ops (durable jobs, slice 1c): a run this tab's stores
 * don't show (`run:<id>`), or a queued start (`wait:<id>`). */
type RunOpType = `run:${string}` | `wait:${string}`;

type OpType = RunOpType | "background-batch" | "angle-rescore" | "pdf" | "scoring" | "trawl" | "snowball" | "clustering" | "monitor" | "bib-import" | "smart-trawl" | "seed-discovery" | "field-debate" | "coverage-audit" | "gap-fill" | "zotero-bulk-import";

interface OperationBarProps {
  onCancelPdf: () => void;
  onCancelScoring: () => void;
  onCancelSnowball?: () => void;
  onCancelMonitor?: () => void;
  // Optional: not every mount site has a concept in scope. Threads through
  // to useScoringBatchStatus, which no-ops (enabled: false) without one.
  conceptId?: string;
}

export function OperationBar({ onCancelPdf, onCancelScoring, onCancelSnowball, onCancelMonitor, conceptId }: OperationBarProps) {
  // Activity panel (slice 1b): the bar's expanded state. A pill click opens
  // it scrolled to that job; the Activity button opens it as a whole.
  const panelOpen = useActivityStore((s) => s.open);
  const panelFocus = useActivityStore((s) => s.focus);
  const openPanel = useActivityStore((s) => s.openPanel);
  const closePanel = useActivityStore((s) => s.closePanel);
  const recordActivity = useActivityStore((s) => s.record);
  const session = useActivityStore((s) => s.session);
  const seenAt = useActivityStore((s) => (conceptId ? s.seenAt[conceptId] : undefined));
  const activity = useActivity(conceptId, panelOpen ? "open" : "closed").data;
  const durable = !!useCurrentUser().data?.features?.durable_jobs;
  const [stoppingIds, setStoppingIds] = useState<Set<string>>(() => new Set());
  const runs = useMemo(() => (durable ? activity?.jobs ?? NO_RUNS : NO_RUNS), [durable, activity?.jobs]);
  const closeActivity = useCallback(() => closePanel(conceptId), [closePanel, conceptId]);
  const navigate = useNavigate();

  // PDF store
  const pdfIsRunning = usePdfStore((s) => s.isRetrieving);
  const pdfIsCompleted = usePdfStore((s) => s.isCompleted);
  const pdfError = usePdfStore((s) => s.error);
  const pdfProgress = usePdfStore((s) => s.progress);
  const resetPdf = usePdfStore((s) => s.resetRetrieving);

  // Scoring store
  const scoringIsRunning = useScoringStore((s) => s.isScoring);
  const scoringIsCompleted = useScoringStore((s) => s.isCompleted);
  const scoringError = useScoringStore((s) => s.error);
  const scoringProgress = useScoringStore((s) => s.progress);
  const resetScoring = useScoringStore((s) => s.resetScoring);
  // Tier 4 hybrid batch: durable poll survives deploys/reloads the SSE
  // stream doesn't. Hook is called unconditionally (rules of hooks) —
  // internally gated by `enabled: !!conceptId`.
  const batchStatus = useScoringBatchStatus(conceptId ?? "");
  const cancelBatch = useCancelScoringBatch(conceptId ?? "");
  const batch = batchStatus.data;
  const batchEstimate = useRunEstimate(
    batch?.state === "pending" ? conceptId : undefined, "scoring_batch", batch?.tail_total,
  ).data;
  const batchUsual = batchEstimate?.basis === "measured" ? formatUsual(batchEstimate.low, batchEstimate.high) : null;

  // Angle rescore (facets 1c): one coalesced pass per concept, shown here
  // instead of the tab bar. Pending covers both the queued request and its
  // hand-off to a Batch job; failed = retries exhausted.
  const { data: concept } = useConcept(conceptId ?? "");
  const retryAngle = useRetryAngleRescore(conceptId ?? "");
  const [angleFailDismissed, setAngleFailDismissed] = useState(false);
  const anglePending = !!concept?.angle_rescore_pending;
  const angleFailed = !!concept?.angle_rescore_failed && !angleFailDismissed;
  useEffect(() => {
    if (concept?.angle_rescore_pending) setAngleFailDismissed(false);
  }, [concept?.angle_rescore_pending]);

  // Trawl store
  const trawlIsRunning = useTrawlStore((s) => s.isTrawling);
  const trawlIsCompleted = useTrawlStore((s) => s.isCompleted);
  const trawlError = useTrawlStore((s) => s.error);
  const trawlProgress = useTrawlStore((s) => s.progress);
  const cancelTrawl = useTrawlStore((s) => s.cancelTrawl);
  const resetTrawl = useTrawlStore((s) => s.resetTrawl);

  // Snowball store
  const snowballIsRunning = useSnowballStore((s) => s.isSnowballing);
  const snowballIsCompleted = useSnowballStore((s) => s.isCompleted);
  const snowballError = useSnowballStore((s) => s.error);
  const snowballProgress = useSnowballStore((s) => s.progress);
  const cancelSnowball = useSnowballStore((s) => s.cancelSnowball);
  const resetSnowball = useSnowballStore((s) => s.resetSnowball);

  // Clustering store
  const clusteringIsRunning = useClusteringStore((s) => s.isClustering);
  const clusteringIsCompleted = useClusteringStore((s) => s.isCompleted);
  const clusteringError = useClusteringStore((s) => s.error);
  const clusteringProgress = useClusteringStore((s) => s.progress);
  const resetClustering = useClusteringStore((s) => s.resetClustering);

  // BibTeX import store
  const bibImportIsRunning = useBibtexImportStore((s) => s.isImporting);
  const bibImportIsCompleted = useBibtexImportStore((s) => s.isCompleted);
  const bibImportError = useBibtexImportStore((s) => s.error);
  const bibImportProgress = useBibtexImportStore((s) => s.progress);
  const resetBibImport = useBibtexImportStore((s) => s.resetImport);

  // Monitor store
  const monitorIsRunning = useMonitorStore((s) => s.isRunning);
  const monitorIsCompleted = useMonitorStore((s) => s.isCompleted);
  const monitorError = useMonitorStore((s) => s.error);
  const monitorProgress = useMonitorStore((s) => s.progress);
  const resetMonitor = useMonitorStore((s) => s.resetMonitor);

  // Spot Trawl store
  const smartTrawlIsRunning = useSmartTrawlStore((s) => s.isRunning);
  const smartTrawlIsCompleted = useSmartTrawlStore((s) => s.isCompleted);
  const smartTrawlError = useSmartTrawlStore((s) => s.error);
  const smartTrawlProgress = useSmartTrawlStore((s) => s.progress);
  const resetSmartTrawl = useSmartTrawlStore((s) => s.reset);

  // Seed Discovery store
  // Only concept-owned runs show here; an Add-angle sheet run shows in the sheet (1c-3).
  const seedDiscoveryIsRunning = useSeedDiscoveryStore((s) => s.owner === "concept" && s.isRunning);
  const seedDiscoveryIsCompleted = useSeedDiscoveryStore((s) => s.owner === "concept" && s.isCompleted);
  const seedDiscoveryError = useSeedDiscoveryStore((s) => (s.owner === "concept" ? s.error : null));
  const seedDiscoveryPhase = useSeedDiscoveryStore((s) => s.phase);
  const seedDiscoveryResolve = useSeedDiscoveryStore((s) => s.resolveProgress);
  const seedDiscoveryAnalyze = useSeedDiscoveryStore((s) => s.analyzeProgress);
  const dismissSeedDiscoveryPill = useSeedDiscoveryStore((s) => s.dismissPill);

  // Field Debate generation store (self-contained — pill cancels via apiClient)
  const fdConceptId = useFieldDebateGenerationStore((s) => s.conceptId);
  const fdJobId = useFieldDebateGenerationStore((s) => s.jobId);
  const fdIsPending = useFieldDebateGenerationStore((s) => s.isPending);
  const fdError = useFieldDebateGenerationStore((s) => s.error);
  const fdStep = useFieldDebateGenerationStore((s) => s.step);
  const fdStepLabel = useFieldDebateGenerationStore((s) => s.stepLabel);
  const fdTotalSteps = useFieldDebateGenerationStore((s) => s.totalSteps);
  const fdPass4Completed = useFieldDebateGenerationStore((s) => s.pass4Completed);
  const fdPass4Total = useFieldDebateGenerationStore((s) => s.pass4Total);
  const resetFieldDebate = useFieldDebateGenerationStore((s) => s.reset);

  const cancelFieldDebate = useCallback(async () => {
    // Reset + abort SYNCHRONOUSLY so the pill disappears immediately on
    // click — including during preflight when there's no server-side jobId
    // yet to POST a cancel for. Without this, cancel during the
    // active-probe / cancel-if-needed / POST window before jobId arrives
    // was a no-op (old `if (!fdJobId) return` guard) and the pill stayed
    // "Running" until the regen completed naturally.
    const ctrl = useFieldDebateGenerationStore.getState().cancelController;
    ctrl?.abort();
    resetFieldDebate();
    if (!fdConceptId || !fdJobId) return;
    try {
      await apiClient.post(
        `api/concepts/${fdConceptId}/field-debate/generate/${fdJobId}/cancel`,
      );
    } catch {
      // Best-effort — SSE will deliver the cancelled event.
    }
  }, [fdConceptId, fdJobId, resetFieldDebate]);

  // Field-debate completion is derived (no separate isCompleted boolean today)
  const fdIsCompleted = useFieldDebateGenerationStore((s) => s.isCompleted);

  // Coverage Audit (gap analysis) generation store — mirrors field-debate.
  const gaConceptId = useGapAnalysisGenerationStore((s) => s.conceptId);
  const gaJobId = useGapAnalysisGenerationStore((s) => s.jobId);
  const gaIsPending = useGapAnalysisGenerationStore((s) => s.isPending);
  const gaError = useGapAnalysisGenerationStore((s) => s.error);
  const gaStep = useGapAnalysisGenerationStore((s) => s.step);
  const gaStepLabel = useGapAnalysisGenerationStore((s) => s.stepLabel);
  const gaTotalSteps = useGapAnalysisGenerationStore((s) => s.totalSteps);
  const gaIsCompleted = useGapAnalysisGenerationStore((s) => s.isCompleted);
  const resetGapAnalysis = useGapAnalysisGenerationStore((s) => s.reset);

  const cancelGapAnalysis = useCallback(async () => {
    const ctrl = useGapAnalysisGenerationStore.getState().cancelController;
    ctrl?.abort();
    resetGapAnalysis();
    // Also clear the legacy gap-analysis-store so the AnalyzeActivePanel
    // pending UI (which reads gapStore.isPending, not the generation
    // store) doesn't stay stuck on the "Auditing…" spinner indefinitely.
    // AbortError swallows the normal handleError/handleComplete path
    // that would otherwise flip isPending=false.
    useGapAnalysisStore.getState().cancel();
    if (!gaConceptId || !gaJobId) return;
    try {
      await apiClient.post(
        `api/concepts/${gaConceptId}/clustering/coverage-audit/generate/${gaJobId}/cancel`,
      );
    } catch {
      // Best-effort — SSE will deliver the cancelled event.
    }
  }, [gaConceptId, gaJobId, resetGapAnalysis]);

  // Gap-fill (Phase 2 — per-gap Find Papers pipeline)
  const gfConceptId = useGapFillStore((s) => s.conceptId);
  const gfJobId = useGapFillStore((s) => s.jobId);
  const gfIsPending = useGapFillStore((s) => s.isPending);
  const gfError = useGapFillStore((s) => s.error);
  const gfStep = useGapFillStore((s) => s.step);
  const gfStepLabel = useGapFillStore((s) => s.stepLabel);
  const gfTotalSteps = useGapFillStore((s) => s.totalSteps);
  const gfIsCompleted = useGapFillStore((s) => s.isCompleted);
  const gfIsOpen = useGapFillStore((s) => s.isOpen);
  const resetGapFill = useGapFillStore((s) => s.reset);

  const cancelGapFill = useCallback(async () => {
    const ctrl = useGapFillStore.getState().cancelController;
    ctrl?.abort();
    resetGapFill();
    if (!gfConceptId || !gfJobId) return;
    try {
      await apiClient.post(
        `api/concepts/${gfConceptId}/gap-fill/generate/${gfJobId}/cancel`,
      );
    } catch {
      // Best-effort — SSE delivers cancelled event.
    }
  }, [gfConceptId, gfJobId, resetGapFill]);

  // Pill click on a completed gap-fill: re-open the modal (instead of
  // the default expand behavior) so the user can recover from an
  // accidental click-outside / ESC. They preserved the candidates by
  // soft-closing; clicking the pill brings the modal back.
  const reopenGapFill = useCallback(() => {
    useGapFillStore.getState().openModal();
  }, []);

  // Zotero bulk-import store
  const zbiJobId = useZoteroBulkImportStore((s) => s.jobId);
  const zbiStatus = useZoteroBulkImportStore((s) => s.status);
  const zbiFetched = useZoteroBulkImportStore((s) => s.fetched);
  const zbiAdded = useZoteroBulkImportStore((s) => s.added);
  const zbiReusedEvents = useZoteroBulkImportStore((s) => s.reusedEvents);
  const zbiDuplicates = useZoteroBulkImportStore((s) => s.duplicates);
  const zbiFailed = useZoteroBulkImportStore((s) => s.failed);
  const resetZbi = useZoteroBulkImportStore((s) => s.reset);

  // Route through the shared cancelBulkImport so the OperationBar pill,
  // the modal Cancel button, and any future entry point all share one
  // failure-aware path: server-derived terminal status, 409 reconciliation,
  // 15s safety-net timeout, and toast on cancel-POST failure. Importantly,
  // do NOT reset the store before the backend acknowledges — that strands
  // the safety net (no jobId to reconcile) and hides any work the backend
  // still completed.
  const zbiQueryClient = useQueryClient();

  // Field Debate "usually X–Y": the estimate is bucketed by corpus size,
  // read from whichever papers page is already cached (no extra fetch).
  const fdPaperTotal = fdIsPending && conceptId
    ? Math.max(0, ...zbiQueryClient
        .getQueriesData<PaperListResponse>({ queryKey: ["papers", conceptId] })
        .map(([, d]) => d?.total_count ?? d?.total ?? 0))
    : 0;
  // The server records an FD run's size as the scoped set it maps, which it
  // caps near 400 — a bigger corpus would land in the wrong bucket.
  const fdSize = Math.min(fdPaperTotal, FD_SCOPE_CAP);
  const fdEstimate = useRunEstimate(
    fdSize > 0 ? conceptId : undefined, "field_debate", fdSize || undefined,
  ).data;
  const fdUsual = fdEstimate?.basis === "measured" ? formatUsual(fdEstimate.low, fdEstimate.high) : null;
  const cancelZbi = useCallback(
    async () => cancelBulkImport(zbiQueryClient),
    [zbiQueryClient],
  );

  // Auto-fade completed operations after 5s
  const completedOps: [boolean, () => void][] = [
    [pdfIsCompleted, resetPdf],
    [scoringIsCompleted, resetScoring],
    [trawlIsCompleted, resetTrawl],
    [snowballIsCompleted, resetSnowball],
    [clusteringIsCompleted, resetClustering],
    [monitorIsCompleted, resetMonitor],
    [bibImportIsCompleted, resetBibImport],
    [smartTrawlIsCompleted, resetSmartTrawl],
    [seedDiscoveryIsCompleted, dismissSeedDiscoveryPill],
    [fdIsCompleted, resetFieldDebate],
    [gaIsCompleted, resetGapAnalysis],
    [zbiStatus === "done", resetZbi],
    // Gap-fill is intentionally NOT in this list — losing the candidate
    // payload after 5s would destroy ~30s of LLM work (S2 + score_batch
    // + caption_gap_fit). Pill stays sticky until the user explicitly
    // dismisses by clicking it (handled in the pill onClick below).
  ];

  useEffect(() => {
    const timers = completedOps
      .filter(([completed]) => completed)
      .map(([, reset]) => setTimeout(reset, 5000));
    return () => timers.forEach(clearTimeout);
  }, [pdfIsCompleted, scoringIsCompleted, trawlIsCompleted, snowballIsCompleted, clusteringIsCompleted, monitorIsCompleted, bibImportIsCompleted, smartTrawlIsCompleted, seedDiscoveryIsCompleted, fdIsCompleted, gaIsCompleted, zbiStatus]);

  // Auto-fade an errored seed-discovery pill after 8s. Errors aren't in the
  // completed-fade list above, so without this a failed run (e.g. a dropped
  // SSE stream) leaves a permanent red 0/0 bar with no way to clear it short
  // of a page reload. 8s (vs 5s for completions) keeps the failure visible
  // a beat longer; the toast already carries the detail.
  useEffect(() => {
    if (!seedDiscoveryError) return;
    const t = setTimeout(dismissSeedDiscoveryPill, 8000);
    return () => clearTimeout(t);
  }, [seedDiscoveryError, dismissSeedDiscoveryPill]);

  const toggleExpand = useCallback((op: OpType) => {
    if (panelOpen && panelFocus === op) closeActivity();
    else openPanel(conceptId, op);
  }, [panelOpen, panelFocus, closeActivity, openPanel, conceptId]);

  // Build list of active operations
  type OpConfig = {
    type: OpType;
    icon: ReactNode;
    label: string;
    color: string;
    completed: number;
    total: number;
    detail: string | null;
    status: OperationStatus;
    /** Omit when the job can't be cancelled — the detail panel then hides Cancel. */
    onCancel?: () => void;
    /** When set, Cancel asks this in place first. */
    cancelPrompt?: string;
    onDismiss: () => void;
    countLabel?: string;
    uncounted?: boolean;
    startedAt?: number | null;
    usual?: string | null;
    /** Server-backed op: pace measured from the job's real start. */
    paceFrom?: { t: number; done: number } | null;
    /** A cancel/remove was sent: show it as stopping, no second click. */
    stopping?: boolean;
    message?: string | null;
    stats?: string | null;
    action?: { label: string; onClick: () => void };
    /** Don't pop the detail panel open when this op starts (long, quiet jobs). */
    noAutoExpand?: boolean;
    /** A queued start: Waiting group, cancel = Remove. */
    waiting?: boolean;
    /** The store op type a server-backed op stands in for. */
    covers?: string;
  };

  const ops: OpConfig[] = [];

  const batchStartedAt = batch?.created_at ? Date.parse(batch.created_at) : null;
  const angleBatch = batch?.state === "pending" && batch.mode === "angle_rescore";

  if (anglePending || angleFailed) {
    ops.push({
      type: "angle-rescore",
      icon: <Layers className="size-4" />,
      label: anglePending ? "Refreshing angle scores" : "Angle scores",
      color: "#cba6f7",
      completed: 0,
      total: 0,
      detail: null,
      status: anglePending ? "running" : "error",
      uncounted: true,
      countLabel: angleBatch && batch?.tail_total ? `${batch.tail_total} papers` : "",
      startedAt: angleBatch ? batchStartedAt : null,
      usual: angleBatch ? batchUsual : null,
      message: "couldn't refresh after 3 tries",
      action: { label: "Retry", onClick: () => retryAngle.mutate() },
      onDismiss: () => setAngleFailDismissed(true),
      noAutoExpand: true,
    });
  }

  if (batch?.state === "pending" && !angleBatch) {
    ops.push({
      type: "background-batch",
      icon: <Star className="size-4" />,
      label: "Background scoring",
      color: "#a855f7",
      completed: 0,
      total: 0,
      detail: "Large scoring runs use Anthropic's Batch API at half cost.",
      status: "running",
      uncounted: true,
      countLabel: `${batch.tail_total} papers`,
      startedAt: batchStartedAt,
      usual: batchUsual,
      onCancel: () => cancelBatch.mutate(),
      cancelPrompt: "Stop background scoring? Papers not yet scored stay unscored.",
      onDismiss: () => {},
      noAutoExpand: true,
    });
  }

  if (monitorIsRunning || monitorIsCompleted || monitorError) {
    ops.push({
      type: "monitor",
      icon: <Radio className="size-4" />,
      label: "Living Lit",
      color: "#10b981",
      completed: monitorProgress.currentPaper,
      total: monitorProgress.totalPapers,
      detail: monitorProgress.message || monitorProgress.stage || null,
      status: monitorError ? "error" : monitorIsCompleted ? "completed" : "running",
      onCancel: () => onCancelMonitor?.(),
      onDismiss: resetMonitor,
    });
  }

  if (pdfIsRunning || pdfIsCompleted || pdfError) {
    ops.push({
      type: "pdf",
      icon: <Download className="size-4" />,
      label: "PDF Retrieval",
      color: "#3b82f6",
      completed: pdfProgress.completed,
      total: pdfProgress.total,
      detail: pdfProgress.currentPaper ? `Now: ${pdfProgress.currentPaper}` : null,
      status: pdfError ? "error" : pdfIsCompleted ? "completed" : "running",
      stats: pdfProgress.completed > 0 ? `${pdfProgress.succeeded} found · ${pdfProgress.failed} no open PDF` : null,
      message: pdfProgress.total > 0 ? `${pdfProgress.completed} of ${pdfProgress.total} tried · ${pdfProgress.succeeded} found` : null,
      onCancel: onCancelPdf,
      cancelPrompt: `Stop now? The ${Math.max(0, pdfProgress.total - pdfProgress.completed)} papers not yet tried stay without PDFs.`,
      onDismiss: resetPdf,
    });
  }

  if (scoringIsRunning || scoringIsCompleted || scoringError) {
    ops.push({
      type: "scoring",
      icon: <Star className="size-4" />,
      label: "Scoring",
      color: "#a855f7",
      completed: scoringProgress.scored,
      total: scoringProgress.total,
      detail: scoringProgress.statusMessage || null,
      status: scoringError ? "error" : scoringIsCompleted ? "completed" : "running",
      onCancel: onCancelScoring,
      onDismiss: resetScoring,
    });
  }

  if (trawlIsRunning || trawlIsCompleted || trawlError) {
    ops.push({
      type: "trawl",
      icon: <Play className="size-4" />,
      label: "Trawl",
      color: "#22c55e",
      completed: trawlProgress.papersProcessed,
      total: trawlProgress.totalPapers,
      detail: trawlProgress.currentPaperTitle || null,
      status: trawlError ? "error" : trawlIsCompleted ? "completed" : "running",
      message: trawlIsCompleted ? trawlProgress.statusMessage || null : null,
      onCancel: cancelTrawl,
      onDismiss: resetTrawl,
    });
  }

  if (snowballIsRunning || snowballIsCompleted || snowballError) {
    ops.push({
      type: "snowball",
      icon: <Pickaxe className="size-4" />,
      label: "Snowball",
      color: "#f59e0b",
      completed: snowballProgress.completed,
      total: snowballProgress.total,
      detail: snowballProgress.currentSeed
        ? `Seed: ${snowballProgress.currentSeed.slice(0, 50)}${snowballProgress.currentSeed.length > 50 ? "..." : ""}${snowballProgress.discoveredCount > 0 ? ` · ${snowballProgress.discoveredCount} found` : ""}`
        : null,
      status: snowballError ? "error" : snowballIsCompleted ? "completed" : "running",
      message: snowballIsCompleted ? snowballProgress.statusMessage || null : null,
      onCancel: () => { cancelSnowball(); onCancelSnowball?.(); },
      onDismiss: resetSnowball,
    });
  }

  if (clusteringIsRunning || clusteringIsCompleted || clusteringError) {
    const STAGE_LABELS: Record<string, string> = {
      idle: "Starting...",
      embedding: "Generating embeddings...",
      clustering: "Running KMeans...",
      labeling: "Generating labels...",
      complete: "Complete",
      error: "Error",
    };
    ops.push({
      type: "clustering",
      icon: <Target className="size-4" />,
      label: "Clustering",
      color: "#06b6d4",
      completed: clusteringProgress.percent,
      total: 100,
      detail: STAGE_LABELS[clusteringProgress.stage] || clusteringProgress.statusMessage || null,
      status: clusteringError ? "error" : clusteringIsCompleted ? "completed" : "running",
      // No cancel endpoint: hide Cancel rather than show a dead button.
      onCancel: undefined,
      onDismiss: resetClustering,
    });
  }

  if (bibImportIsRunning || bibImportIsCompleted || bibImportError) {
    ops.push({
      type: "bib-import",
      icon: <FileText className="size-4" />,
      label: "BibTeX Import",
      color: "#06b6d4",
      completed: bibImportProgress.resolved,
      total: bibImportProgress.total,
      detail: bibImportProgress.statusMessage || null,
      status: bibImportError ? "error" : bibImportIsCompleted ? "completed" : "running",
      // No cancel endpoint: hide Cancel rather than show a dead button.
      onCancel: undefined,
      onDismiss: resetBibImport,
    });
  }

  if (smartTrawlIsRunning || smartTrawlIsCompleted || smartTrawlError) {
    const stepLabel = smartTrawlProgress.step === "fetching_references" ? "Fetching refs"
      : smartTrawlProgress.step === "resolving_citations" ? "Resolving citations"
      : smartTrawlProgress.step === "adding_papers" ? "Adding papers"
      : smartTrawlProgress.step === "done" ? `+${smartTrawlProgress.totalAdded} papers`
      : smartTrawlProgress.step || null;
    ops.push({
      type: "smart-trawl",
      icon: <Sparkles className="size-4" />,
      label: "Spot Trawl",
      color: "#f59e0b",
      completed: smartTrawlProgress.current,
      total: smartTrawlProgress.total,
      detail: smartTrawlProgress.currentPaper
        ? `${smartTrawlProgress.currentPaper.slice(0, 40)}${stepLabel ? ` · ${stepLabel}` : ""}`
        : stepLabel,
      status: smartTrawlError ? "error" : smartTrawlIsCompleted ? "completed" : "running",
      // No cancel endpoint: hide Cancel rather than show a dead button.
      onCancel: undefined,
      onDismiss: resetSmartTrawl,
      countLabel: `${smartTrawlProgress.current}%`,
    });
  }

  if (seedDiscoveryIsRunning || seedDiscoveryIsCompleted || seedDiscoveryError) {
    const PHASE_LABELS: Record<string, string> = {
      searching: "Searching Semantic Scholar...",
      suggesting: "Asking LLM for suggestions...",
      selecting: "Selecting seeds from real papers...",
      resolving: seedDiscoveryResolve
        ? `Resolving ${seedDiscoveryResolve.current}/${seedDiscoveryResolve.total}...`
        : "Resolving papers...",
      analyzing: seedDiscoveryAnalyze
        ? `Analyzing citations ${seedDiscoveryAnalyze.current}/${seedDiscoveryAnalyze.total}...`
        : "Analyzing citations...",
      complete: "Complete",
      error: "Error",
    };
    const progress = seedDiscoveryResolve || seedDiscoveryAnalyze;
    ops.push({
      type: "seed-discovery",
      icon: <Compass className="size-4" />,
      label: "Seed Discovery",
      color: "#8b5cf6",
      completed: progress?.current ?? 0,
      total: progress?.total ?? 0,
      detail: PHASE_LABELS[seedDiscoveryPhase] || null,
      status: seedDiscoveryError ? "error" : seedDiscoveryIsCompleted ? "completed" : "running",
      // No cancel endpoint: hide Cancel rather than show a dead button.
      onCancel: undefined,
      onDismiss: dismissSeedDiscoveryPill,
    });
  }

  if (fdIsPending || fdIsCompleted || fdError) {
    const pass4Active = fdStep === 5 && fdPass4Total > 0;
    const detail = pass4Active
      ? `${fdStepLabel} (${fdPass4Completed}/${fdPass4Total})`
      : fdStepLabel || null;
    ops.push({
      type: "field-debate",
      icon: <Network className="size-4" />,
      label: "Field Debate",
      color: "#8b5cf6",
      completed: pass4Active ? fdPass4Completed : Math.max(0, fdStep),
      total: pass4Active ? fdPass4Total : fdTotalSteps,
      detail,
      status: fdError ? "error" : fdIsCompleted ? "completed" : "running",
      onCancel: cancelFieldDebate,
      onDismiss: resetFieldDebate,
      usual: fdUsual,
      countLabel: pass4Active
        ? undefined
        : `Step ${Math.max(0, fdStep)}/${fdTotalSteps}`,
    });
  }

  if (gaIsPending || gaIsCompleted || gaError) {
    ops.push({
      type: "coverage-audit",
      icon: <BarChart3 className="size-4" />,
      label: "Coverage Audit",
      color: "#74c7ec",
      completed: Math.max(0, gaStep),
      total: gaTotalSteps,
      detail: gaStepLabel || null,
      status: gaError ? "error" : gaIsCompleted ? "completed" : "running",
      onCancel: cancelGapAnalysis,
      onDismiss: resetGapAnalysis,
      countLabel: `Step ${Math.max(0, gaStep)}/${gaTotalSteps}`,
    });
  }

  if (gfIsPending || gfIsCompleted || gfError) {
    ops.push({
      type: "gap-fill",
      icon: <Search className="size-4" />,
      label: "Gap Fill",
      color: "#cba6f7",
      completed: Math.max(0, gfStep),
      total: gfTotalSteps,
      detail: gfStepLabel || null,
      status: gfError ? "error" : gfIsCompleted ? "completed" : "running",
      onCancel: cancelGapFill,
      onDismiss: resetGapFill,
      countLabel: `Step ${Math.max(0, gfStep)}/${gfTotalSteps}`,
    });
  }

  if (zbiJobId && (zbiStatus === "running" || zbiStatus === "pending")) {
    const zbiProcessed = zbiAdded + zbiReusedEvents + zbiDuplicates + zbiFailed;
    ops.push({
      type: "zotero-bulk-import",
      icon: <Download className="size-4" />,
      label: `Importing ${zbiProcessed} of ${zbiFetched} from Zotero`,
      color: "#8b5cf6",
      completed: zbiProcessed,
      total: zbiFetched,
      detail: null,
      status: "running",
      onCancel: cancelZbi,
      onDismiss: resetZbi,
    });
  }

  // Durable jobs (slice 1c): a run this tab isn't watching — after a reload,
  // on another route, or from another tab — gets its pill from job_runs.
  // Store pills keep their own progress but take the server's start time,
  // so their clock survives navigation.
  if (durable && conceptId) {
    // A store op in any state covers its kind: a run this tab just watched
    // finish may still read "running" until the next poll.
    const liveStoreTypes = new Set(ops.map((o) => o.type as string));
    for (const op of ops) {
      if (op.startedAt != null || op.status !== "running") continue;
      const run = runs.find((r) => r.status === "running" && STORE_OP[r.kind] === op.type && r.started_at);
      if (run?.started_at) op.startedAt = Date.parse(run.started_at);
    }
    const cancelRun = (runId: string) => {
      // The row only changes at the next poll (and a running job's stop lands
      // a moment later), so say "Stopping…" now and take the button away.
      setStoppingIds((s) => new Set(s).add(runId));
      apiClient
        .post(`api/concepts/${conceptId}/jobs/${runId}/cancel`)
        .catch((err: unknown) => {
          // Already gone (finished, or a previous click got there) is success.
          if ((err as { response?: { status?: number } })?.response?.status === 404) return;
          setStoppingIds((s) => {
            const next = new Set(s);
            next.delete(runId);
            return next;
          });
          toast.error("Couldn't stop that job");
        })
        .finally(() => zbiQueryClient.invalidateQueries({ queryKey: ["activity", conceptId] }));
    };
    for (const run of runs) {
      const covers = STORE_OP[run.kind];
      if (run.status === "running") {
        // Shown by its store here, or by the panel's own rows (1b).
        if ((covers && liveStoreTypes.has(covers)) || run.kind === "abstract_backfill") continue;
        ops.push({
          type: `run:${run.id}`,
          icon: <ActivityIcon className="size-4" />,
          label: kindLabel(run.kind),
          color: "#89b4fa",
          completed: run.done ?? 0,
          total: run.total ?? 0,
          uncounted: !run.total,
          countLabel: run.total ? undefined : "running",
          status: "running",
          startedAt: run.started_at ? Date.parse(run.started_at) : null,
          paceFrom: run.started_at ? { t: Date.parse(run.started_at), done: 0 } : null,
          detail: stoppingIds.has(run.id) ? "Stopping…" : run.detail,
          onCancel: CANCELLABLE.has(run.kind) && !stoppingIds.has(run.id) ? () => cancelRun(run.id) : undefined,
          onDismiss: () => {},
          noAutoExpand: true,
          covers,
        });
      } else if (run.status === "waiting") {
        ops.push({
          type: `wait:${run.id}`,
          icon: <Clock className="size-4" />,
          label: `${kindLabel(run.kind)} · waiting`,
          color: "#f9e2af",
          completed: 0,
          total: 0,
          uncounted: true,
          countLabel: "waiting",
          status: "running",
          startedAt: Date.parse(run.created_at),
          onCancel: stoppingIds.has(run.id) ? undefined : () => cancelRun(run.id),
          detail: stoppingIds.has(run.id) ? "Removing…" : "Starts when the one running now finishes",
          onDismiss: () => {},
          noAutoExpand: true,
          waiting: true,
        });
      }
    }
  }

  // Keep the bar expanded by default: whenever the leading running operation
  // changes, auto-open its detail panel. A manual collapse (pill toggle or the
  // detail's collapse button) sets expandedOp to null and persists — this
  // effect only fires when the leading running op itself changes, so it won't
  // re-expand mid-operation and fight the user.
  // A user cancel reaches the stores as a "…cancelled" error. Show it as a
  // neutral, labelled end state; real errors show their reason.
  const errorText: Partial<Record<OpType, string | null>> = {
    monitor: monitorError, pdf: pdfError, scoring: scoringError, trawl: trawlError,
    snowball: snowballError, clustering: clusteringError, "bib-import": bibImportError,
    "smart-trawl": smartTrawlError, "seed-discovery": seedDiscoveryError,
    "field-debate": fdError, "coverage-audit": gaError, "gap-fill": gfError,
  };
  for (const op of ops) {
    if (op.status !== "error" || !(op.type in errorText)) continue;
    const err = errorText[op.type];
    if (isCancelMessage(err)) {
      op.status = "cancelled";
      op.label = `${op.label} cancelled`;
      op.message ??= op.total > 0 ? `${op.completed} of ${op.total} done` : null;
    } else {
      op.message = err;
    }
  }

  const firstRunningType = ops.find((o) => o.status === "running" && !o.noAutoExpand)?.type;
  const prevRunningTypeRef = useRef<OpType | undefined>(undefined);
  useEffect(() => {
    if (firstRunningType !== prevRunningTypeRef.current) {
      // Only a start from an idle bar opens the panel: one job ending while
      // another runs mustn't reopen a panel the user closed.
      const wasIdle = prevRunningTypeRef.current === undefined;
      prevRunningTypeRef.current = firstRunningType;
      if (firstRunningType && wasIdle) openPanel(conceptId, firstRunningType);
    }
  }, [firstRunningType, openPanel, conceptId]);

  // A browser job that ends goes to the panel's Earlier list. Server-recorded
  // work (monitor, Field Debate, batches) comes from GET /activity instead.
  const prevStatusRef = useRef(new Map<OpType, OperationStatus>());
  useEffect(() => {
    const prev = prevStatusRef.current;
    for (const op of ops) {
      if (prev.get(op.type) !== "running" || op.status === "running") continue;
      if (!conceptId || SERVER_RECORDED.has(op.type) || /^(run|wait):/.test(op.type)) continue;
      recordActivity({
        conceptId,
        type: op.type,
        label: op.label.replace(/ cancelled$/, ""),
        outcome: op.status === "completed" ? "success" : op.status === "cancelled" ? "cancelled" : "failed",
        summary: op.status === "completed" ? (op.message ?? op.detail ?? op.stats ?? null) : (op.message ?? null),
        finishedAt: Date.now(),
      });
    }
    prevStatusRef.current = new Map(ops.map((o) => [o.type, o.status]));
  });

  // A batched Field Debate build lands minutes after its tab is gone; the
  // generate hook toasts only the builds it is still watching.
  // Only a durable batch build counts (a live job is one the generate hook
  // toasts itself). Armed from when it was first seen; it lands as a
  // finished row newer than that, which can show up a poll after the build
  // leaves `running` (status commit and timing row are separate writes).
  const fdBatchBuild = !!activity?.running.some((j) => j.kind === "field_debate_build" && j.batch);
  const fdBatchSeenAt = useRef<number | null>(null);
  useEffect(() => {
    if (fdBatchBuild) {
      fdBatchSeenAt.current ??= Date.now();
      return;
    }
    const seen = fdBatchSeenAt.current;
    if (seen == null || !activity || !conceptId) return;
    const landed = activity.finished.find(
      // Margin: our clock and the server's needn't agree to the second.
      (f) => f.kind === "field_debate_build" && Date.parse(f.finished_at) >= seen - 120_000,
    );
    if (landed) {
      fdBatchSeenAt.current = null;
      if (landed.outcome === "success") {
        toastJobFinished(
          conceptId, "Field Debate ready",
          landed.count ? `The map covers ${landed.count} papers.` : "The map is up to date.",
          `fd-build-landed-${landed.finished_at}`,
        );
      }
    } else if (Date.now() - seen > FD_LANDED_GIVE_UP_MS) {
      fdBatchSeenAt.current = null;
    }
  }, [fdBatchBuild, activity, conceptId]);

  // A run that ends while this tab wasn't watching it (no store handler ran):
  // refresh what it changed.
  const runAgainInFlight = useRef(new Set<string>());
  const runningRunIds = useRef({ conceptId, ids: new Map<string, string>() });
  useEffect(() => {
    if (!conceptId) return;
    const now = new Map(runs.filter((r) => r.status === "running").map((r) => [r.id, r.kind]));
    const prev = runningRunIds.current;
    // Switching concepts isn't a run ending.
    if (prev.conceptId === conceptId) {
      for (const [id, kind] of prev.ids) {
        if (!now.has(id)) invalidateAfter(zbiQueryClient, conceptId, kind);
      }
    }
    runningRunIds.current = { conceptId, ids: now };
  }, [runs, conceptId, zbiQueryClient]);

  const unread = unreadCount(activity, seenAt);
  const conceptSession = session.filter((e) => e.conceptId === conceptId);

  // The background-batch and angle-rescore ops come from server state, not
  // an in-memory store, so they appear on a fresh mount (the reload case) and
  // outlive the "scoring" op's own pill. With nothing running, the bar stays
  // as just the Activity button while something new is unread.
  if (ops.length === 0 && !panelOpen && unread === 0) return null;

  const waitingAngle = ["queued", "settling", "deferred", "retrying"].includes(activity?.angle_rescore?.state ?? "");
  const liveOps: LiveOp[] = ops
    .filter((o) => o.status === "running" && !(o.type === "angle-rescore" && waitingAngle))
    .map((o) => ({
      type: o.type, icon: o.icon, label: o.label, color: o.color,
      completed: o.completed, total: o.total, uncounted: o.uncounted, countLabel: o.countLabel,
      detail: o.detail, stats: o.stats, onCancel: o.onCancel, cancelPrompt: o.cancelPrompt,
      waiting: o.waiting, covers: o.covers,
    }));

  const runAgain = (runId: string) => {
    const run = runs.find((r) => r.id === runId);
    const path = run && RUN_AGAIN_PATH[run.kind];
    // One request per click: a second would queue a duplicate (and pay twice).
    if (!run || !path || !conceptId || runAgainInFlight.current.has(runId)) return;
    runAgainInFlight.current.add(runId);
    apiClient
      .post(`api/concepts/${conceptId}/${path}`, { json: run.params, timeout: 120_000 })
      .json<unknown>()
      .then((reply) => {
        if (isQueued(reply)) announceQueued(zbiQueryClient, conceptId, kindLabel(run.kind), reply);
        else toast.success(`${kindLabel(run.kind)} started again`);
      })
      .catch(() => toast.error(`Couldn't start ${kindLabel(run.kind)} again`))
      .finally(() => {
        runAgainInFlight.current.delete(runId);
        zbiQueryClient.invalidateQueries({ queryKey: ["activity", conceptId] });
      });
  };
  const resume = (runId: string) => {
    const run = runs.find((r) => r.id === runId);
    if (!run) return;
    const resumer = useActivityStore.getState().resumers[run.kind];
    if (resumer) resumer(run);
    else toast.info(`Open Curate to resume the ${kindLabel(run.kind).toLowerCase()}`);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] bg-slate-800 border-t border-slate-700 shadow-[0_-2px_10px_rgba(0,0,0,0.35)] animate-in slide-in-from-bottom-2 duration-200"
      role="status"
      aria-live="polite"
      data-tour-target="operation-bar"
    >
      {panelOpen && (
        <ActivityPanel
          conceptName={concept?.name}
          liveOps={liveOps}
          data={activity}
          session={conceptSession}
          focus={panelFocus}
          durable={durable}
          onRunAgain={runAgain}
          onResume={resume}
          onCollapse={closeActivity}
          onShowMonitorPapers={() => {
            closeActivity();
            if (conceptId) {
              navigate({
                to: "/concepts/$conceptId/curate",
                params: { conceptId },
                search: { monitorInboxView: true },
              });
            }
          }}
        />
      )}
      <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
        {ops.map((op) => (
          <OperationPill
            key={op.type}
            icon={op.icon}
            label={op.label}
            color={op.color}
            completed={op.completed}
            total={op.total}
            status={op.status}
            countLabel={op.countLabel}
            uncounted={op.uncounted}
            startedAt={op.startedAt}
            usual={op.usual}
            paceFrom={op.paceFrom}
            message={op.message}
            action={op.action}
            onDismiss={op.onDismiss}
            isExpanded={panelOpen && panelFocus === op.type}
            onClick={() => {
              if (
                op.type === "gap-fill" &&
                op.status === "completed" &&
                !gfIsOpen
              ) {
                reopenGapFill();
              } else if (op.status === "running") {
                toggleExpand(op.type);
              }
            }}
          />
        ))}
        <button
          type="button"
          onClick={() => (panelOpen ? closeActivity() : openPanel(conceptId))}
          aria-expanded={panelOpen}
          className="ml-auto flex items-center gap-1.5 rounded border border-slate-600 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700"
        >
          Activity
          {unread > 0 && !panelOpen && (
            <span className="rounded-full bg-red-400 px-1.5 text-[10px] font-semibold text-slate-900">{unread}</span>
          )}
          <span aria-hidden>{panelOpen ? "▾" : "▴"}</span>
        </button>
      </div>
    </div>
  );
}
